import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { ArrowLeft, Repeat2, RotateCw, ShieldCheck, Trophy } from 'lucide-react';
import NpcEncounterOverlay from './NpcEncounterOverlay';
import useLearningCoaching from '../hooks/useLearningCoaching';
import ScenePlayer from './ScenePlayer';
import Spinner from './Spinner';
import ReviewLevelUpDialog from './ReviewLevelUpDialog';
import { useConfirmDialog } from './ConfirmDialog';
import TriumphReviewSession, { TRIUMPH_REVIEW_TIME_LIMIT } from '../services/TriumphReviewSession';
import { t } from '../i18n';
import './TriumphReviewTraining.css';

const copy = (key, replacements) => t(`practiceUi.reviewTraining.${key}`, undefined, replacements);

export default function TriumphReviewTraining({ cards, scene, access, onClose }) {
  const [session] = useState(() => new TriumphReviewSession(cards, access));
  const { scenes: coachingScenes } = useLearningCoaching('review');
  const [phase, setPhase] = useState('scene');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [sessionScoreGained, setSessionScoreGained] = useState(0);
  const [reason, setReason] = useState('manual');
  const [saveWarning, setSaveWarning] = useState(false);
  const [levelUpCard, setLevelUpCard] = useState(null);
  const [repeatLocked, setRepeatLocked] = useState(false);
  const { confirm, confirmDialog, isOpen: confirming } = useConfirmDialog();
  const elapsedMsRef = useRef(0);
  const runningSinceRef = useRef(null);
  const finishingRef = useRef(false);
  const lastCheckpointRef = useRef(0);
  const blockedPromptRef = useRef(false);
  const panelRef = useRef(null);
  const paused = confirming || Boolean(levelUpCard);
  const blocker = useBlocker(() => phase !== 'done' && !session.complete);

  const currentElapsedMs = useCallback(() => Math.min(TRIUMPH_REVIEW_TIME_LIMIT * 1000,
    elapsedMsRef.current + (runningSinceRef.current === null ? 0 : Date.now() - runningSinceRef.current)), []);

  const finish = useCallback(async (endReason = 'manual') => {
    if (finishingRef.current) return false;
    finishingRef.current = true;
    setReason(endReason);
    setLevelUpCard(null);
    setPhase('saving');
    try {
      await session.save(Math.ceil(currentElapsedMs() / 1000), true);
      setPhase('done');
      return true;
    } catch {
      setPhase('error');
      return false;
    } finally {
      finishingRef.current = false;
    }
  }, [currentElapsedMs, session]);

  const requestEnd = useCallback(async () => {
    if (confirming || repeatLocked || finishingRef.current || phase === 'done' || phase === 'error') return;
    const accepted = await confirm({
      title: copy('endTitle'), message: copy('endMessage'),
      confirmLabel: copy('end'), cancelLabel: copy('keepTraining'), tone: 'warning',
    });
    if (accepted) await finish('manual');
  }, [confirm, confirming, finish, phase, repeatLocked]);

  useEffect(() => {
    if (phase !== 'active' || paused) return undefined;
    runningSinceRef.current = Date.now();
    const timer = setInterval(() => {
      const seconds = Math.floor(currentElapsedMs() / 1000);
      setElapsedSeconds(seconds);
      if (seconds >= TRIUMPH_REVIEW_TIME_LIMIT) {
        void finish('time');
        return;
      }
      const checkpoint = Math.floor(seconds / 30);
      if (checkpoint > lastCheckpointRef.current) {
        lastCheckpointRef.current = checkpoint;
        void session.save(seconds).then(() => setSaveWarning(false)).catch(() => setSaveWarning(true));
      }
    }, 250);
    return () => {
      elapsedMsRef.current = currentElapsedMs();
      runningSinceRef.current = null;
      clearInterval(timer);
    };
  }, [currentElapsedMs, finish, paused, phase, session]);

  useEffect(() => {
    if (blocker.state !== 'blocked' || repeatLocked || blockedPromptRef.current) return;
    blockedPromptRef.current = true;
    void (async () => {
      const accepted = await confirm({
        title: copy('endTitle'), message: copy('endMessage'),
        confirmLabel: copy('end'), cancelLabel: copy('keepTraining'), tone: 'warning',
      });
      if (accepted && await finish('manual')) blocker.proceed();
      else blocker.reset();
      blockedPromptRef.current = false;
    })();
  }, [blocker, confirm, finish, repeatLocked]);

  useEffect(() => {
    if (phase === 'done') return undefined;
    const warnBeforeUnload = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [phase]);

  useEffect(() => {
    if (paused || phase === 'saving' || phase === 'error' || phase === 'done') return undefined;
    const endOnEscape = event => {
      if (event.key !== 'Escape' || event.repeat || event.defaultPrevented) return;
      event.preventDefault();
      void requestEnd();
    };
    window.addEventListener('keydown', endOnEscape);
    return () => window.removeEventListener('keydown', endOnEscape);
  }, [paused, phase, requestEnd]);

  useEffect(() => { if (!paused) panelRef.current?.focus(); }, [phase, paused]);

  const containFocus = event => {
    if (event.key !== 'Tab' || paused || event.defaultPrevented) return;
    const controls = Array.from(panelRef.current.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"]'));
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
      event.preventDefault(); last?.focus();
    } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panelRef.current)) {
      event.preventDefault(); first?.focus();
    }
  };

  const start = () => { session.start(); setPhase('active'); };
  const recordResult = useCallback(result => {
    const reward = session.recordResult(result);
    setSessionScoreGained(session.getSummary().scoreGained);
    return reward;
  }, [session]);
  const repeat = useCallback(result => {
    const reward = session.repeat(result);
    setSessionScoreGained(session.getSummary().scoreGained);
    return reward;
  }, [session]);
  const answerViewed = useCallback(result => {
    const reward = session.answerViewed(result);
    setSessionScoreGained(session.getSummary().scoreGained);
    return reward;
  }, [session]);
  const summary = session.getSummary();

  return <div ref={panelRef} tabIndex={-1} onKeyDown={containFocus}>
    {phase === 'scene' ? <ScenePlayer
      scene={scene} variant="opponent" onContinue={start} continueLabel={copy('start')}
      isPaused={confirming} captureGlobalKeys={false}
    /> : phase === 'active' ? <NpcEncounterOverlay
      cards={cards} scene={scene} coachingScenes={coachingScenes} reviewMode elapsedSeconds={elapsedSeconds} sessionScoreGained={sessionScoreGained}
      isPaused={paused} canUseTts={access.entitlements?.canUseTts !== false}
      onComplete={() => { void finish('cards'); }} onSkip={requestEnd}
      onCardResult={recordResult} onPracticeRepeat={repeat} onAnswerViewed={answerViewed} onLevelUp={setLevelUpCard}
      onRepeatLockChange={setRepeatLocked}
      reviewNotice={saveWarning ? copy('checkpointFailed') : null}
    /> : <section className="triumph-review-result" role="dialog" aria-modal="true" aria-label={copy('title')}
      tabIndex={-1}>
      <div className="triumph-review-result-content">
        {phase === 'saving' ? <Spinner message={copy('saving')} /> : phase === 'error' ? <>
          <h1>{copy('saveFailedTitle')}</h1><p role="alert">{copy('saveFailed')}</p>
          <button type="button" onClick={() => { void finish(reason); }}>{copy('retrySave')}</button>
        </> : <>
          <header className="triumph-review-result-header triumph-review-result-reveal">
            <h1>{copy('complete')}</h1>
            <p>{copy(`reason${reason[0].toUpperCase()}${reason.slice(1)}`)}</p>
          </header>
          <dl className="triumph-review-summary">
            <div className="triumph-review-result-reveal">
              <ShieldCheck size={22} strokeWidth={2.4} aria-hidden="true" />
              <dt>{copy('success')}</dt><dd>{summary.success}</dd>
            </div>
            <div className="triumph-review-result-reveal">
              <Repeat2 size={22} strokeWidth={2.4} aria-hidden="true" />
              <dt>{copy('fail')}</dt><dd>{summary.fail}</dd>
            </div>
            <div className="triumph-review-result-reveal">
              <RotateCw size={22} strokeWidth={2.4} aria-hidden="true" />
              <dt>{copy('repeats')}</dt><dd>{summary.repeatCount}</dd>
            </div>
            <div className="triumph-review-result-reveal">
              <Trophy size={22} strokeWidth={2.4} aria-hidden="true" />
              <dt>{copy('points')}</dt><dd>+{summary.scoreGained}</dd>
            </div>
          </dl>
          <button type="button" className="triumph-review-result-back triumph-review-result-reveal"
            onClick={() => onClose(Array.from(session.cards.values()))}>
            <ArrowLeft size={20} strokeWidth={2.4} aria-hidden="true" />
            {copy('back')}
          </button>
        </>}
      </div>
    </section>}
    {levelUpCard && <ReviewLevelUpDialog card={levelUpCard} onClose={() => setLevelUpCard(null)}
      onOpenNote={async noteId => {
        if (await finish('manual')) onClose(Array.from(session.cards.values()), noteId);
      }} />}
    {confirmDialog}
  </div>;
}
